import { Button, Card, Group, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation } from '@tanstack/react-query'
import { IconFlask } from '@tabler/icons-react'
import { useOrpc } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

// Data Migrations card for loading the curated shared lab panels.
//
// No organization picker: the panels are curated platform data and go to every
// practice, like the sibling migrations on this page. The result names how many
// practices were written to, and any that failed, so a partial run is visible.
//
// Structure deliberately mirrors the sibling migration cards on this page.
export function SeedSharedPanelsCard() {
  const orpc = useOrpc()

  const seed = useMutation(
    orpc.seedSharedPanels.mutationOptions({
      onSuccess: (result) => {
        const failed = result.failures.length
        notifications.show({
          color: failed ? 'yellow' : 'green',
          title: failed ? 'Shared panels partly seeded' : 'Shared panels seeded',
          message:
            `${result.seeded} panel(s) written to ${result.organizations.length} organization(s).` +
            (failed
              ? ` ${failed} could not be seeded: ${result.failures
                  .map((f) => `${f.organizationName} (${f.error})`)
                  .join('; ')}`
              : ''),
          autoClose: false,
          withCloseButton: true,
        })
      },
      onError: (error) => {
        logError('super', 'Failed to seed shared lab panels', error)
        notifications.show({
          color: 'red',
          title: 'Seeding failed',
          message: error instanceof Error ? error.message : 'Unknown error',
          autoClose: false,
          withCloseButton: true,
        })
      },
    }),
  )

  return (
    <Card withBorder shadow="sm" radius="md" p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <div>
            <Text fw={600}>Seed Shared Lab Panels</Text>
            <Text c="dimmed" size="sm">
              Loads the curated Labcorp and Quest order sets so every practitioner sees them under
              &ldquo;Practice Panels&rdquo; when placing a lab order. Runs for every organization.
              Safe to re-run — panels are matched by id and replaced, not duplicated.
            </Text>
          </div>
          <Button
            leftSection={<IconFlask size={18} />}
            loading={seed.isPending}
            onClick={() => seed.mutate({})}
          >
            Run Seed
          </Button>
        </Group>

        {seed.data && (
          <Text size="sm" c="dimmed">
            Last run: {seed.data.seeded} panel(s) written to {seed.data.organizations.length}{' '}
            organization(s)
            {seed.data.organizations.filter((o) => o.created).length > 0 &&
              `, ${seed.data.organizations.filter((o) => o.created).length} seeded for the first time`}
            {seed.data.failures.length > 0 && `, ${seed.data.failures.length} failed`}.
          </Text>
        )}
      </Stack>
    </Card>
  )
}
